





































































































































import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { min, upperFirst, includes } from 'lodash';
import { BPagination } from 'bootstrap-vue';
import Multiselect from '@reciprocity/multiselect';
import ColumnsSettings from '@/components/listing/components/columns/ColumnsSettings';
// import Favorites from '@/components/listing/components/favorites/Favorites';
import Filters from '@/components/listing/components/filters/Filters';
import Actions from '@/components/listing/components/actions/Actions';
import BookmarkLink from '@/components/bookmark_link/BookmarkLink';
import { ZenFilter } from './components/filters/types';
import BulkEdit from './components/edit/BulkEdit';
import ZenTable from '@/components/listing/ZenTable.vue';
import { downloadAttachments } from '@/components/listing/utils';

import { Column, ColumnSortDirection, DataRow, SortBy } from './types';
import { getRequest } from '../../utils/client/client';

export default Vue.extend({
  name: 'Listing',
  components: {
    Actions,
    BookmarkLink,
    BPagination,
    ColumnsSettings,
    // Favorites,
    Filters,
    Multiselect,
    ZenTable,
    BulkEdit,
  },
  props: {
    title: {
      type: String,
      default: null,
    },
    objectType: {
      // In ModelMetaSchema.modelSingular
      type: String,
      required: true,
    },
    data: {
      type: Array as () => DataRow[],
      required: true,
    },
    page: {
      type: Number,
      default: 1,
    },
    oldFavorite: {
      type: Object,
      default: null,
    },
    userPreferences: {
      type: Object,
      default: () => ({}),
    },
    columns: {
      type: Array as () => Column[],
      required: true,
    },
    activePageSize: {
      type: Number,
      default: 25,
    },
    defaultActiveColumns: {
      type: Array,
      required: true,
    },
    activeColumns: {
      type: Array,
      required: true,
    },
    sort: {
      type: Object as () => SortBy,
      required: true,
    },
    simpleSearch: {
      type: Boolean,
      default: false,
    },
    activeFilters: {
      type: Array as () => ZenFilter[],
      required: true,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    pagination: {
      type: Boolean,
      default: true,
    },
    pageSizes: {
      type: Array as () => Number[],
      default: () => [25, 50, 100, 200],
    },
    pageSizeControl: {
      type: Boolean,
      default: true,
    },
    listingId: {
      type: String,
      default: undefined,
    },
    enableEditing: {
      type: Boolean,
      default: false,
    },
    showMinimal: {
      type: Boolean,
      default: false,
    },
    enableColReorder: {
      type: Boolean,
      default: false,
    },
    resizableCols: {
      type: Boolean,
      default: false,
    },
    enableActions: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedObjects: <object>[],
    };
  },
  computed: {
    ...mapGetters(['csrfToken', 'modelMetaFindBy']),

    // TODO: TEMP favorites implementation, remove when transitioning to new favorites
    computedOldFavorite(): { id: number; title: string } {
      if (!this.oldFavorite) {
        return {
          id: null,
          title: '',
        };
      }

      return this.oldFavorite;
    },

    bulkEditMode(): boolean {
      return this.selectedObjects.length > 0;
    },
    paginationInfo(): string {
      const allCount: number = this.page * this.activePageSize;
      const limitCount: number = min([this.totalCount, allCount]) as number;

      if (this.totalCount > 0) {
        return `Showing ${allCount - this.activePageSize + 1} to ${limitCount} of ${this.totalCount}`;
      } else {
        return 'Showing 0 entries';
      }
    },
  },
  methods: {
    ...mapActions({
      showModal: 'modals/show',
      showAlert: 'alerts/show',
    }),
    refetch(): void {
      this.$emit('refetch');
    },
    oldFavoriteChange(data): void {
      this.$emit('oldFavoriteChange', data);
    },
    userPreferencesChange(preferences): void {
      this.$emit('userPreferencesChange', preferences);
      this.resetSelectedObjects();
    },
    pageChange(page: number): void {
      this.$emit('pageChange', page);
    },
    pageSizeChange(pageSize: number): void {
      this.$emit('pageSizeChange', pageSize);
      this.resetSelectedObjects();
    },
    columnsReset(): void {
      this.$emit('columnsReset');
      this.resetSelectedObjects();
    },
    columnsChange(columns: Array<string>): void {
      this.$emit('columnsChange', columns);
      this.resetSelectedObjects();
    },
    filtersChange(filters: Array<Filter>): void {
      this.$emit('filtersChange', filters);
      this.resetSelectedObjects();
    },
    sortChange(col: string): void {
      let direction: ColumnSortDirection = ColumnSortDirection.Asc;

      if (col == this.sort.by) {
        direction = this.sort.direction == ColumnSortDirection.Asc ? ColumnSortDirection.Desc : ColumnSortDirection.Asc;
      }

      this.$emit('sortChange', {
        by: col,
        direction,
      });
      this.resetSelectedObjects();
    },
    refresh(): void {
      this.$router.go(0);
    },
    onInlineEditClick(object: string, column: Column, attrValue: any) {
      this.editHandler([object], column, false, attrValue);
    },
    onBulkEditClick(objects: string[], column: Column) {
      this.editHandler(objects, column, true);
    },
    async editHandler(objects: string[], column: Column, isBulk: boolean, attrValue?: any) {
      try {
        await this.showModal({
          type: 'InlineEditCard',
          data: {
            data: isBulk ? {} : attrValue,
            column,
            objects,
            objectType: this.objectType,
          },
        });
      } catch {
        // User closed the modal
      }
    },
    onInlineMapClick(object: string, col?: Column) {
      this.mapHandler([object], col);
    },
    onBulkCreateTaskClick(objects: string[]) {
      this.createTaskHandler(objects);
    },
    onBulkMapClick(objects: string[]) {
      // TODO: Improvement - support direct object type selection for bulk map
      this.mapHandler(objects);
    },
    async createTaskHandler(objects: string[]) {
      let created = false;

      // Backend expects the following format of objects: "<object_type>: <slug>\n ..."
      let prefixedObjects = objects.map(el => this.objectType + ': ' + el);
      let urlSuffix = encodeURIComponent(JSON.stringify({ related_objects: prefixedObjects.join('\n') }));

      try {
        await this.showModal({
          type: 'NewObject',
          data: {
            url: '/sor/new_object/Task?prefill=' + urlSuffix,
            type: 'task',
          },
        });

        created = true;
      } catch (e) {
        // User closed the modal
      }

      if (created) {
        this.$emit('refetch');
      }
    },
    mapHandler(objects: string[], col?: Column) {
      const objectTypeToMapTo = key => upperFirst(key.split('.').length > 1 ? key.split('.')[1] : key);
      const relevantStr = 'relevant';

      let mapToObjectType = null;
      if (col !== undefined) {
        if (col.key.startsWith(relevantStr)) {
          mapToObjectType = col.key.substr(relevantStr.length);
        } else {
          mapToObjectType = col.key;
        }
      }

      this.$router.push({
        name: 'objectMapper',
        params: {
          objectIds: objects.map(o => o.split(':')[1]).join(','),
          // TODO: remove modelMeta
          leftType: this.modelMetaFindBy('modelSingular', this.objectType)['modelSingular'],
          rightType: col
            ? this.modelMetaFindBy('modelPlural', objectTypeToMapTo(mapToObjectType))['modelSingular']
            : undefined,
          redirectUrl: this.$route.fullPath,
          backUrl: this.$route.fullPath,
        },
      });
    },
    onBulkDeleteClick(objects: string[]) {
      this.deleteHandler(objects);
    },
    async deleteHandler(objects: string[]) {
      try {
        await this.showModal({
          type: 'DeleteCard',
          data: {
            objects,
            objectType: this.objectType,
          },
        });
        this.resetSelectedObjects();
      } catch {
        // User closed the modal
      }
    },
    onBulkCalculateClick(objects: string[]) {
      this.calculateHandler(objects);
    },
    async calculateHandler(objects: string[]) {
      try {
        await this.showModal({
          type: 'CalculateCard',
          data: {
            objects,
            objectType: this.objectType,
          },
        });
        this.resetSelectedObjects();
      } catch {
        // User closed the modal
      }
    },
    onBulkCloseClick(objects: string[]) {
      this.surveyCloseHandler(objects);
    },
    async surveyCloseHandler(objects: string[]) {
      const url = '/survey/close/bulk:' + objects.map(o => o.split(':')[1]).join(',') + '/';
      const modelMeta = this.modelMetaFindBy('modelSingular', this.objectType);
      let objectType = objects.length > 1 ? modelMeta.titlePlural : modelMeta.titleSingular;
      try {
        await this.showModal({
          type: 'CloseCard',
          data: {
            bulkCount: objects.length,
            axiosRequest: {
              method: 'POST',
              url: url,
              headers: {
                'X-CSRF-Token': this.csrfToken,
              },
            },
            objectType: objectType,
          },
        });
        this.resetSelectedObjects();
        this.refetch();
      } catch {
        // User closed the modal
      }
    },
    onSelectedRowChange(rows: string[]) {
      this.$emit('selectedRowChange', rows);
      this.selectedObjects = this.data.filter(el => includes(rows, el.id));
    },
    toggleRowDetails(props): void {
      props.row.meta.rowDetails.active = !props.row.meta.rowDetails.active;
      this.$emit('toggleRowDetails', props.row.id);
    },
    resetSelectedObjects() {
      this.selectedObjects = [];
    },
    sendSurveysHandler(objectId: string) {
      let objectData = objectId.split(':');
      // Use Vue Router since survey sending page is a SPA
      this.$router.push('/survey_sending/new/' + objectData[0] + '/' + objectData[1]);
    },
    viewSurveysHandler(objectId: string) {
      let objectData = objectId.split(':');
      const linkToSurveys = '/sor/info/' + objectData[0] + '/' + objectData[1] + '/sent_surveys';
      // Use legacy navigation since object info page is not SPA
      // TODO: Change to Vue Router navigation once info page is SPA
      window.location.href = linkToSurveys;
    },
    cloneSurveyEmailTemplateHandler(objectId: string) {
      let objectData = objectId.split(':');
      const linkToDuplicate = '/survey/emailtemplates/' + objectData[1] + '/duplicate/';
      window.location.href = linkToDuplicate;
    },
    previewSurveyEmailTemplate(objectId: string) {
      let objectData = objectId.split(':');
      const linkToPreview = '/survey/preview_email_template/' + objectData[1];
      window.location.href = linkToPreview;
    },
    duplicateSurveyTemplate(objectId: string) {
      let objectData = objectId.split(':');
      const linkToDuplicate = '/survey_builder/' + objectData[1] + '/duplicate';
      window.location.href = linkToDuplicate;
    },
    previewSurveyTemplate(objectId: string) {
      let objectData = objectId.split(':');
      const linkToPreview = '/survey/preview/' + objectData[1];
      window.location.href = linkToPreview;
    },
    exportSurveyTemplate(objectId: string) {
      let objectData = objectId.split(':');
      const linkToExport = '/api/export/surveys/' + objectData[1];
      window.location.href = linkToExport;
    },
    exportSurveyResponse(objectId: string) {
      let objectData = objectId.split(':');
      const linkToExport = '/api/export/surveys/responses/' + objectData[1];
      window.location.href = linkToExport;
    },
    async downloadSurveyResponse(objectId: string) {
      let objectData = objectId.split(':');
      let response = await downloadAttachments(this.csrfToken, this.objectType, objectData[1]);

      if (response.type != 'success') {
        this.showAlert({
          type: response.type,
          message: response.message,
        });
      }
    },
    resendSurveyResponse(objectId: string) {
      const row = this.data.find(row => row.id == objectId);
      const publicId = row.publicId.value;

      getRequest({ url: '/survey_sending/api/can_resend?public_id=' + publicId })
        .then(responseData => {
          if (responseData.resend_error !== '') {
            this.showAlert({
              type: 'error',
              message: responseData.resend_error,
            });
            return;
          }

          window.location.href = '/survey_sending/resend/' + publicId;
        })
        .catch(() => {
          this.showAlert({
            type: 'error',
            message: 'Oops, something went wrong.',
          });
        });
    },
    closeSurveyResponse(objectId: string) {
      this.surveyCloseHandler([objectId]);
    },
    onActionClick(actionKey: string, objectId: string) {
      switch (actionKey) {
        case 'sendSurveys':
          this.sendSurveysHandler(objectId);
          break;
        case 'viewSurveys':
          this.viewSurveysHandler(objectId);
          break;
        case 'delete':
          this.deleteHandler([objectId]);
          break;
        case 'cloneSurveyEmailTemplate':
          this.cloneSurveyEmailTemplateHandler(objectId);
          break;
        case 'previewSurveyEmailTemplate':
          this.previewSurveyEmailTemplate(objectId);
          break;
        case 'duplicateSurveyTemplate':
          this.duplicateSurveyTemplate(objectId);
          break;
        case 'previewSurveyTemplate':
          this.previewSurveyTemplate(objectId);
          break;
        case 'exportSurveyTemplate':
          this.exportSurveyTemplate(objectId);
          break;
        case 'exportSurveyResponse':
          this.exportSurveyResponse(objectId);
          break;
        case 'downloadSurveyResponse':
          this.downloadSurveyResponse(objectId);
          break;
        case 'resendSurveyResponse':
          this.resendSurveyResponse(objectId);
          break;
        case 'closeSurveyResponse':
          this.closeSurveyResponse(objectId);
          break;
        default:
          // TODO: TEMP throw error instead
          // eslint-disable-next-line no-console
          console.error('Handler for this action was not set');
          break;
      }
    },
    onColumnSizeChange(col, size) {
      this.$emit('columnSizeChange', col, size);
    },
  },
});
